import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Search, MessageCircle, Loader2, Edit,
  Mic, FileText, ChevronDown, X, Check, ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom searchable dropdown component (same as ForumNewQuestionPage)
const SearchableSelect = ({ 
  placeholder, 
  value, 
  onChange, 
  options, 
  groupBy, 
  renderOption,
  emptyMessage = "No options found",
  testId,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  const selectedOption = options.find(o => o.id === value);
  
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const searchLower = search.toLowerCase();
    return options.filter(o => 
      o.name.toLowerCase().includes(searchLower) ||
      (o.shortName && o.shortName.toLowerCase().includes(searchLower))
    );
  }, [options, search]);
  
  const groupedOptions = useMemo(() => {
    if (!groupBy) return { '': filteredOptions };
    
    const groups = {};
    for (const option of filteredOptions) {
      const group = groupBy(option);
      if (!groups[group]) groups[group] = [];
      groups[group].push(option);
    }
    return groups;
  }, [filteredOptions, groupBy]);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center justify-between w-full px-3 py-2 text-left bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0A7EA4] ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-gray-50'
        }`}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? (renderOption ? renderOption(selectedOption) : selectedOption.name) : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {Object.keys(groupedOptions).length === 0 || filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">{emptyMessage}</div>
            ) : (
              Object.entries(groupedOptions).map(([group, groupOptions]) => (
                <div key={group}>
                  {group && (
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">
                      {group}
                    </div>
                  )}
                  {groupOptions.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        onChange(option.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 ${
                        value === option.id ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-700'
                      }`}
                    >
                      {renderOption ? renderOption(option) : option.name}
                      {value === option.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Hierarchical topic selector (same as ForumNewQuestionPage)
const TopicTreeSelect = ({ topics, value, onChange, placeholder, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  const hierarchicalTopics = useMemo(() => {
    const parents = topics.filter(t => !t.parent_topic_id);
    const children = topics.filter(t => t.parent_topic_id);
    
    return parents.map(parent => ({
      ...parent,
      children: children.filter(c => c.parent_topic_id === parent.id)
    }));
  }, [topics]);
  
  // All parents expanded by default
  const [expandedParents, setExpandedParents] = useState(() => {
    return new Set(hierarchicalTopics.map(t => t.id));
  });
  
  // Update expanded parents when topics change
  useEffect(() => {
    setExpandedParents(new Set(hierarchicalTopics.map(t => t.id)));
  }, [hierarchicalTopics]);
  
  const selectedTopic = topics.find(t => t.id === value);
  
  const filteredTopics = useMemo(() => {
    if (!search.trim()) return hierarchicalTopics;
    
    const searchLower = search.toLowerCase();
    const filtered = [];
    
    for (const parent of hierarchicalTopics) {
      const parentMatches = parent.name.toLowerCase().includes(searchLower);
      const matchingChildren = parent.children?.filter(c => 
        c.name.toLowerCase().includes(searchLower)
      ) || [];
      
      if (parentMatches || matchingChildren.length > 0) {
        filtered.push({
          ...parent,
          children: parentMatches ? parent.children : matchingChildren
        });
      }
    }
    return filtered;
  }, [hierarchicalTopics, search]);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const toggleParent = (parentId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };
  
  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-left bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0A7EA4]"
      >
        <span className={selectedTopic ? 'text-gray-900' : 'text-gray-500'}>
          {selectedTopic ? selectedTopic.name : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange('none');
                setIsOpen(false);
                setSearch('');
              }}
              className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 ${
                value === 'none' || !value ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-500 italic'
              }`}
            >
              No specific topic
              {(value === 'none' || !value) && <Check className="w-4 h-4 ml-auto" />}
            </button>
            
            {filteredTopics.length === 0 && search ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No topics match "{search}"</div>
            ) : (
              filteredTopics.map(parent => (
                <div key={parent.id}>
                  <div className="flex items-center">
                    {parent.children && parent.children.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleParent(parent.id)}
                        className="p-2 hover:bg-gray-100"
                      >
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                          expandedParents.has(parent.id) || search ? 'rotate-90' : ''
                        }`} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        onChange(parent.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={`flex-1 flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-gray-100 ${
                        value === parent.id ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-700'
                      } ${!parent.children?.length ? 'pl-10' : ''}`}
                    >
                      <span className="font-medium">{parent.name}</span>
                      {value === parent.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  </div>
                  
                  {/* Child topics - always visible since parents are expanded by default */}
                  {(expandedParents.has(parent.id) || search) && parent.children?.map(child => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => {
                        onChange(child.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={`flex items-center gap-2 w-full pl-12 pr-3 py-2 text-sm text-left hover:bg-gray-100 ${
                        value === child.id ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-600'
                      }`}
                    >
                      {child.name}
                      {value === child.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ForumEditQuestionPage = () => {
  const navigate = useNavigate();
  const { questionId } = useParams();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  
  const [question, setQuestion] = useState(null);
  const [functions, setFunctions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  // Fetch the question
  const fetchQuestion = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/forum/questions/${questionId}`);
      const q = response.data.question;
      setQuestion(q);
      setTitle(q.title);
      setContent(q.content);
      setSelectedTopic(q.topic_id || 'none');
    } catch (error) {
      console.error('Failed to fetch question:', error);
      if (error.response?.status === 404) {
        toast.error('Question not found');
        navigate('/forum');
      } else {
        toast.error('Failed to load question');
      }
    } finally {
      setLoading(false);
    }
  }, [questionId, navigate]);

  const fetchFunctions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      const filtered = response.data.filter(f => f.grade === preferences.grade);
      setFunctions(filtered);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  }, [preferences.grade]);

  const fetchTopics = useCallback(async () => {
    if (!question?.function_id) {
      setTopics([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/topics`, {
        params: { function_id: question.function_id }
      });
      setTopics(response.data);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  }, [question?.function_id]);

  useEffect(() => {
    fetchQuestion();
    fetchFunctions();
  }, [fetchQuestion, fetchFunctions]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // Check if user can edit
  const canEdit = useMemo(() => {
    if (!question || !user) return false;
    const isOwner = question.user_id === user.id;
    const isAdmin = user.role === 'admin' || user.role === 'sub_admin';
    const isVerified = question.status === 'verified';
    
    // Users can't edit verified questions, admins can
    if (isVerified && !isAdmin) return false;
    
    return isOwner || isAdmin;
  }, [question, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || title.length < 10) {
      toast.error('Title must be at least 10 characters');
      return;
    }
    
    if (!content.trim() || content.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    
    try {
      setSaving(true);
      await axios.put(`${API}/forum/questions/${questionId}`, {
        title: title.trim(),
        content: content.trim(),
        topic_id: selectedTopic === 'none' ? null : (selectedTopic || null)
      });
      
      toast.success('Question updated successfully!');
      navigate(`/forum/question/${questionId}`);
    } catch (error) {
      console.error('Failed to update question:', error);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  // Function options for display
  const functionOptions = useMemo(() => {
    return functions.map(f => ({
      id: f.id,
      name: f.name,
      shortName: `F${f.function_number}${f.function_suffix || ''} - ${f.name}`,
      examType: f.exam_type || 'orals',
      functionNumber: f.function_number
    }));
  }, [functions]);

  const groupByExamType = (option) => {
    return option.examType === 'written' ? '✏️ Written Functions' : '🎤 Oral Functions';
  };

  const renderFunctionOption = (option) => (
    <div className="flex items-center gap-2">
      {option.examType === 'written' ? (
        <FileText className="w-4 h-4 text-purple-500" />
      ) : (
        <Mic className="w-4 h-4 text-[#0A7EA4]" />
      )}
      <span>F{option.functionNumber} - {option.name}</span>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
        </div>
      </Layout>
    );
  }

  if (!question) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto text-center py-12">
          <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Question Not Found</h2>
          <p className="text-gray-500 mb-4">The question you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/forum')}>Back to Forum</Button>
        </div>
      </Layout>
    );
  }

  if (!canEdit) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto text-center py-12">
          <Edit className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Cannot Edit</h2>
          <p className="text-gray-500 mb-4">
            {question.status === 'verified' 
              ? 'This question has been verified and cannot be edited.'
              : 'You don\'t have permission to edit this question.'}
          </p>
          <Button onClick={() => navigate(`/forum/question/${questionId}`)}>Back to Question</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-3xl mx-auto">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(`/forum/question/${questionId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Question
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#0A7EA4]" />
              Edit Question
            </CardTitle>
            <CardDescription>
              Update your question details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Function (read-only) */}
              <div className="space-y-2">
                <Label>Function</Label>
                <SearchableSelect
                  placeholder="Select a function"
                  value={question.function_id}
                  onChange={() => {}}
                  options={functionOptions}
                  groupBy={groupByExamType}
                  renderOption={renderFunctionOption}
                  disabled={true}
                  testId="function-select"
                />
                <p className="text-xs text-gray-500">Function cannot be changed after creation</p>
              </div>

              {/* Topic Selection */}
              {topics.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="topic">Topic (Optional)</Label>
                  <TopicTreeSelect
                    topics={topics}
                    value={selectedTopic}
                    onChange={setSelectedTopic}
                    placeholder="Select a topic"
                    testId="topic-select"
                  />
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Question Title *</Label>
                <Input
                  id="title"
                  placeholder="What's your question? Be specific and clear..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                  data-testid="title-input"
                />
                <p className="text-xs text-gray-500">{title.length}/200 characters</p>
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label htmlFor="content">Description *</Label>
                <Textarea
                  id="content"
                  placeholder="Provide more details about your question..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  data-testid="content-input"
                />
                <p className="text-xs text-gray-500">{content.length}/5000 characters</p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/forum/question/${questionId}`)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                  disabled={saving}
                  data-testid="save-question-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ForumEditQuestionPage;
